/**
 * QueBoardConfigEditor - 깨보드 설정 편집 모달
 *
 * 수식별로 기능 토글, 튜토리얼 모듈 선택, 캐릭터 선택을 관리합니다.
 * 기능별 문제 프리셋에서 클릭하면 수식 + 기능 + 튜토리얼이 자동 설정됩니다.
 */
import { useState, useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Plus, Trash2, Loader2, Save, GripVertical } from 'lucide-react'
import { toast } from 'sonner'
import { quetureContentApi } from '@/api/quetureContent'
import type { QueBoardConfigEntry, QueBoardCharacter } from '@/api/quetureContent'
import { FEATURED_PROBLEMS, findCurriculumEntry, entryToFeatures } from '@quebon/shared-learning'
import type { FeatureCategory } from '@quebon/shared-learning'

const DEFAULT_ENTRY: QueBoardConfigEntry = {
  problem: { left: 3, right: 5 },
  features: { tie: true, teleport5: true, teleport10: true, bank: true, indicator: true, customInput: false },
  tutorial: { enabled: false, modules: [], autoStart: false },
  character: 'queda',
}

const TUTORIAL_MODULES = [
  { id: 'teleport10', label: '텔레포트(10)' },
  { id: 'teleport5', label: '텔레포트(5)' },
  { id: 'fatTie', label: '뚱십 묶기' },
  { id: 'bank', label: '은행' },
]

const CHARACTERS: { id: QueBoardCharacter; label: string }[] = [
  { id: 'queda', label: '깨다' },
  { id: 'chaeli', label: '챌리' },
  { id: 'ddami', label: '따미' },
  { id: 'random', label: '랜덤' },
]

interface Props {
  quetureId: string
  onClose: () => void
}

export function QueBoardConfigEditor({ quetureId, onClose }: Props) {
  const queryClient = useQueryClient()
  const [entries, setEntries] = useState<QueBoardConfigEntry[]>([])
  const [loaded, setLoaded] = useState(false)
  const [expandedPreset, setExpandedPreset] = useState<number | null>(null)

  const { data: fetchedConfig, isLoading } = useQuery({
    queryKey: ['queboard-config', quetureId],
    queryFn: () => quetureContentApi.getQueboardConfig(quetureId),
  })

  useEffect(() => {
    if (fetchedConfig && !loaded) {
      setEntries(fetchedConfig.length > 0 ? fetchedConfig : [{ ...DEFAULT_ENTRY }])
      setLoaded(true)
    }
  }, [fetchedConfig, loaded])

  const saveMutation = useMutation({
    mutationFn: () => quetureContentApi.saveQueboardConfig(quetureId, entries),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      toast.success('깨보드 설정 저장 완료')
      onClose()
    },
    onError: () => toast.error('저장 실패'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => quetureContentApi.deleteQueboardConfig(quetureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      toast.success('깨보드 설정 삭제 완료')
      onClose()
    },
    onError: () => toast.error('삭제 실패'),
  })

  const updateEntry = useCallback((index: number, updater: (e: QueBoardConfigEntry) => QueBoardConfigEntry) => {
    setEntries((prev) => prev.map((e, i) => (i === index ? updater(e) : e)))
  }, [])

  const addEntry = useCallback(() => {
    setEntries((prev) => [...prev, { ...DEFAULT_ENTRY }])
  }, [])

  const removeEntry = useCallback((index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const toggleModule = useCallback((entryIndex: number, moduleId: string) => {
    updateEntry(entryIndex, (e) => {
      const modules = e.tutorial.modules.includes(moduleId)
        ? e.tutorial.modules.filter((m) => m !== moduleId)
        : [...e.tutorial.modules, moduleId]
      return { ...e, tutorial: { ...e.tutorial, modules } }
    })
  }, [updateEntry])

  const applyPreset = useCallback((entryIndex: number, cat: FeatureCategory, left: number, right: number) => {
    const entry = findCurriculumEntry(left, right)
    if (!entry) return
    updateEntry(entryIndex, (e) => ({
      ...e,
      problem: { left, right },
      features: entryToFeatures(entry),
      tutorial: { ...e.tutorial, enabled: true, modules: [cat.moduleId] },
    }))
    setExpandedPreset(null)
  }, [updateEntry])

  if (isLoading || !loaded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl p-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">깨보드 설정</h2>
            <p className="text-sm text-gray-500 font-mono">{quetureId}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {entries.map((entry, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-700">수식 #{idx + 1}</span>
                  <span className="text-xs text-gray-400">
                    {entry.problem.left} × {entry.problem.right} = {entry.problem.left * entry.problem.right}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setExpandedPreset(expandedPreset === idx ? null : idx)}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                      expandedPreset === idx
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50'
                    }`}
                  >
                    기능별 문제
                  </button>
                  {entries.length > 1 && (
                    <button
                      onClick={() => removeEntry(idx)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* 기능별 문제 선택기 */}
              {expandedPreset === idx && (
                <div className="border border-indigo-200 rounded-lg p-3 bg-white space-y-2.5">
                  <p className="text-xs text-gray-500">클릭하면 수식 + 기능 + 튜토리얼이 자동 설정됩니다</p>
                  {FEATURED_PROBLEMS.map((cat) => (
                    <div key={cat.category} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${cat.dotColor}`} />
                        <span className="text-xs font-semibold text-gray-600">{cat.category}</span>
                        <span className="text-xs text-gray-400">{cat.description}</span>
                        <span className="text-xs text-gray-300">({cat.problems.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {cat.problems.map((p) => {
                          const isActive = entry.problem.left === p.left && entry.problem.right === p.right
                          return (
                            <button
                              key={`${cat.category}-${p.left}x${p.right}`}
                              onClick={() => applyPreset(idx, cat, p.left, p.right)}
                              className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                                isActive ? cat.activeColor : `bg-white ${cat.color}`
                              }`}
                            >
                              {p.left} × {p.right}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 수식 (수동 입력) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">왼쪽 수 (left)</label>
                  <input
                    type="number"
                    min={1}
                    max={9}
                    value={entry.problem.left}
                    onChange={(e) =>
                      updateEntry(idx, (en) => ({
                        ...en,
                        problem: { ...en.problem, left: Number(e.target.value) || 1 },
                      }))
                    }
                    className="w-full mt-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">오른쪽 수 (right)</label>
                  <input
                    type="number"
                    min={1}
                    max={9}
                    value={entry.problem.right}
                    onChange={(e) =>
                      updateEntry(idx, (en) => ({
                        ...en,
                        problem: { ...en.problem, right: Number(e.target.value) || 1 },
                      }))
                    }
                    className="w-full mt-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* 기능 설정 */}
              <div>
                <label className="text-xs font-medium text-gray-600">기능 설정</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(Object.keys(entry.features) as Array<keyof typeof entry.features>).map((key) => (
                    <label key={key} className="flex items-center gap-1.5 px-2.5 py-1 border rounded-lg cursor-pointer select-none text-xs hover:bg-white">
                      <input
                        type="checkbox"
                        checked={entry.features[key]}
                        onChange={(e) =>
                          updateEntry(idx, (en) => ({
                            ...en,
                            features: { ...en.features, [key]: e.target.checked },
                          }))
                        }
                        className="w-3.5 h-3.5 text-indigo-600 rounded"
                      />
                      {key === 'tie' ? '묶기' : key === 'teleport5' ? '텔레포트5' : key === 'teleport10' ? '텔레포트10' : key === 'bank' ? '은행' : key === 'indicator' ? '표시등' : '문제 커스텀'}
                    </label>
                  ))}
                </div>
              </div>

              {/* 튜토리얼 */}
              <div>
                <div className="flex items-center gap-3">
                  <label className="text-xs font-medium text-gray-600">튜토리얼</label>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={entry.tutorial.enabled}
                      onChange={(e) =>
                        updateEntry(idx, (en) => ({
                          ...en,
                          tutorial: { ...en.tutorial, enabled: e.target.checked },
                        }))
                      }
                      className="w-3.5 h-3.5 text-indigo-600 rounded"
                    />
                    <span className="text-xs text-gray-600">사용</span>
                  </label>
                  {entry.tutorial.enabled && (
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={entry.tutorial.autoStart}
                        onChange={(e) =>
                          updateEntry(idx, (en) => ({
                            ...en,
                            tutorial: { ...en.tutorial, autoStart: e.target.checked },
                          }))
                        }
                        className="w-3.5 h-3.5 text-indigo-600 rounded"
                      />
                      <span className="text-xs text-gray-600">자동 시작</span>
                    </label>
                  )}
                </div>
                {entry.tutorial.enabled && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {TUTORIAL_MODULES.map((mod) => (
                      <button
                        key={mod.id}
                        onClick={() => toggleModule(idx, mod.id)}
                        className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                          entry.tutorial.modules.includes(mod.id)
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                        }`}
                      >
                        {mod.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 캐릭터 */}
              <div>
                <label className="text-xs font-medium text-gray-600">캐릭터</label>
                <div className="flex gap-2 mt-1">
                  {CHARACTERS.map((char) => (
                    <button
                      key={char.id}
                      onClick={() => updateEntry(idx, (en) => ({ ...en, character: char.id }))}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        entry.character === char.id
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      {char.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* 수식 추가 버튼 */}
          <button
            onClick={addEntry}
            className="flex items-center gap-1.5 px-4 py-2 w-full justify-center text-sm border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
          >
            <Plus className="w-4 h-4" /> 수식 추가
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={() => {
              if (confirm('깨보드 설정을 삭제하시겠습니까?')) deleteMutation.mutate()
            }}
            className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
          >
            설정 삭제
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              취소
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              저장
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
